import { GraphQLClient } from 'graphql-request';
import { env } from '../lib/env';

/**
 * GraphQL error response structure from AppSync
 */
export interface GraphQLError {
  message: string;
  locations?: Array<{ line: number; column: number }>;
  path?: Array<string | number>;
  extensions?: {
    code?: string;
    errorType?: string;
    errorInfo?: unknown;
  };
}

export interface GraphQLResponse<T = unknown> {
  data?: T;
  errors?: GraphQLError[];
}

/**
 * Custom error class for GraphQL errors
 */
export class GraphQLRequestError extends Error {
  constructor(
    message: string,
    public errors?: GraphQLError[],
    public statusCode?: number,
  ) {
    super(message);
    this.name = 'GraphQLRequestError';
  }
}

/**
 * Authentication mode for GraphQL requests
 */
export type GraphQLAuthMode = 'api-key' | 'cognito' | 'auto';

/**
 * Map of GraphQL operations to their required authentication method
 * Based on schema.graphql directives:
 * - @aws_api_key: Public routes (getDog, getDogs, getLitter, getLitters, addUser, contact)
 * - @aws_cognito_user_pools: Admin routes (getUser, getUsers, addDog, editDog, deleteDog, etc.)
 */
const OPERATION_AUTH_MAP: Record<string, 'api-key' | 'cognito'> = {
  getDog: 'api-key',
  getDogs: 'api-key',
  getLitter: 'api-key',
  getLitters: 'api-key',
  addUser: 'api-key',
  contact: 'api-key',
  getUser: 'cognito',
  getUsers: 'cognito',
  addDog: 'cognito',
  editDog: 'cognito',
  deleteDog: 'cognito',
  addLitter: 'cognito',
  editLitter: 'cognito',
  deleteLitter: 'cognito',
  editUser: 'cognito',
  deleteUser: 'cognito',
  generatePresignedUrl: 'cognito',
};

/**
 * Extract field name from GraphQL query/mutation string
 * The field name (e.g., getDogs, addDog) is what determines the auth method,
 * not the operation name (e.g., GetDogsQuery, AddDogMutation)
 * Handles both query and mutation types, with or without operation name
 */
function extractOperationName(query: string): string | null {
  const normalized = query
    .replace(/#[^\n]*/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  const withOperationName = normalized.match(/(?:query|mutation)\s+\w+\s*\([^)]*\)\s*\{\s*(\w+)/i);
  if (withOperationName && withOperationName[1]) {
    return withOperationName[1];
  }

  const withOperationNameNoVars = normalized.match(/(?:query|mutation)\s+\w+\s*\{\s*(\w+)/i);
  if (withOperationNameNoVars && withOperationNameNoVars[1]) {
    return withOperationNameNoVars[1];
  }

  const withoutOperationName = normalized.match(/(?:query|mutation)\s*\{\s*(\w+)/i);
  if (withoutOperationName && withoutOperationName[1]) {
    return withoutOperationName[1];
  }

  const braceMatch = normalized.match(/\{\s*(\w+)/i);
  if (braceMatch && braceMatch[1]) {
    return braceMatch[1];
  }

  return null;
}

/**
 * Determine the required authentication method for a GraphQL operation
 * @param query - GraphQL query/mutation string
 * @returns The required auth method ('api-key' or 'cognito'), or null if unknown
 */
function determineAuthMethod(query: string): 'api-key' | 'cognito' | null {
  const operationName = extractOperationName(query);
  if (!operationName) {
    console.warn('[GraphQL Auth] Could not extract operation name from query:', {
      queryPreview: query.substring(0, 200),
    });
    return null;
  }

  const authMethod = OPERATION_AUTH_MAP[operationName] || null;
  if (!authMethod) {
    console.warn('[GraphQL Auth] Unknown operation:', operationName);
  } else {
    console.log('[GraphQL Auth] Detected operation:', operationName, '->', authMethod);
  }

  return authMethod;
}

/**
 * Create GraphQL client instance for web-admin
 * Note: Authentication headers are added dynamically in executeGraphQL based on the operation
 */
function createGraphQLClient(): GraphQLClient {
  const endpoint = env.VITE_APP_GRAPHQL_URL;

  if (!endpoint) {
    throw new Error('VITE_APP_GRAPHQL_URL environment variable is required');
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  return new GraphQLClient(endpoint, {
    headers,
    method: 'POST',
  });
}

/**
 * GraphQL client instance
 */
export const graphqlClient = createGraphQLClient();

/**
 * Execute a GraphQL query or mutation with authentication
 * @param query - GraphQL query/mutation string
 * @param variables - Variables for the query/mutation
 * @param authMode - Authentication mode ('api-key', 'cognito', or 'auto')
 * @param getAccessToken - Optional callback to get Cognito ID token (required for 'cognito' mode)
 * @returns Promise resolving to the response data
 * @throws GraphQLRequestError if the request fails or contains errors
 */
export async function executeGraphQL<T = unknown>(
  query: string,
  variables?: Record<string, unknown>,
  authMode: GraphQLAuthMode = 'auto',
  getAccessToken?: () => Promise<string | null>,
): Promise<T> {
  try {
    const headers: Record<string, string> = {};

    let useCognitoAuth = false;
    let useApiKeyAuth = false;

    if (authMode === 'auto') {
      const requiredAuth = determineAuthMethod(query);
      if (requiredAuth === 'cognito') {
        authMode = 'cognito';
      } else if (requiredAuth === 'api-key') {
        authMode = 'api-key';
      } else {
        console.warn('Could not determine auth method from query. Falling back to API key.', {
          query: query.substring(0, 100),
        });
        authMode = 'api-key';
      }
    }

    if (authMode === 'cognito') {
      if (!getAccessToken) {
        throw new GraphQLRequestError(
          'Cognito authentication required but getAccessToken callback not provided.',
        );
      }
      const token = await getAccessToken();
      if (!token) {
        throw new GraphQLRequestError('Authentication required. Please sign in.');
      }
      headers.Authorization = `Bearer ${token}`;
      useCognitoAuth = true;
    } else if (authMode === 'api-key') {
      const apiKey = env.VITE_APP_GRAPHQL_API_KEY;
      if (!apiKey) {
        throw new GraphQLRequestError('API key is required but not configured.');
      }
      headers['x-api-key'] = apiKey;
      useApiKeyAuth = true;
    } else {
      console.warn('Unexpected authMode value:', authMode, '- falling back to API key');
      const apiKey = env.VITE_APP_GRAPHQL_API_KEY;
      if (apiKey) {
        headers['x-api-key'] = apiKey;
        useApiKeyAuth = true;
      }
    }

    if (useCognitoAuth && useApiKeyAuth) {
      console.error(
        'Both Cognito and API key auth were set. This should not happen. Removing API key header.',
      );
      delete headers['x-api-key'];
    }

    const client = new GraphQLClient(env.VITE_APP_GRAPHQL_URL, {
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      method: 'POST',
    });

    const response = await client.request<GraphQLResponse<T>>(query, variables);

    if (response.errors && response.errors.length > 0) {
      const errorMessages = response.errors.map((err) => err.message).join(', ');

      const authError = response.errors.find(
        (err) =>
          err.extensions?.errorType === 'UnauthorizedException' ||
          err.extensions?.code === 'UNAUTHENTICATED' ||
          err.message.includes('Unauthorized'),
      );

      if (authError) {
        throw new GraphQLRequestError(
          'Your session has expired. Please sign in again.',
          response.errors,
          401,
        );
      }

      throw new GraphQLRequestError(`GraphQL errors: ${errorMessages}`, response.errors);
    }

    return response as unknown as T;
  } catch (error) {
    if (error instanceof GraphQLRequestError) {
      throw error;
    }

    if (error && typeof error === 'object' && 'response' in error) {
      const graphqlError = error as { response?: GraphQLResponse<T>; status?: number };
      if (graphqlError.response?.errors) {
        const authError = graphqlError.response.errors.find(
          (err) =>
            err.extensions?.errorType === 'UnauthorizedException' ||
            err.extensions?.code === 'UNAUTHENTICATED' ||
            err.message.includes('Unauthorized'),
        );

        if (authError) {
          throw new GraphQLRequestError(
            'Your session has expired. Please sign in again.',
            graphqlError.response.errors,
            graphqlError.status || 401,
          );
        }

        throw new GraphQLRequestError(
          `GraphQL errors: ${graphqlError.response.errors.map((e) => e.message).join(', ')}`,
          graphqlError.response.errors,
          graphqlError.status,
        );
      }
    }

    if (error instanceof Error) {
      if (error.message.includes('timeout') || error.message.includes('ECONNABORTED')) {
        throw new GraphQLRequestError('Request timeout. Please try again.');
      }
      if (error.message.includes('Network') || error.message.includes('fetch')) {
        throw new GraphQLRequestError('Network error. Please check your connection and try again.');
      }
      throw new GraphQLRequestError(error.message);
    }

    throw new GraphQLRequestError('An unexpected error occurred');
  }
}

/**
 * Execute a GraphQL query or mutation with automatic authentication
 * This function uses auto-detection based on the operation name from the schema
 * @param query - GraphQL query/mutation string
 * @param variables - Variables for the query/mutation
 * @param getAccessToken - Callback to get Cognito ID token
 * @param _requireAuth - DEPRECATED: Kept for backward compatibility but ignored. Always uses auto-detection.
 * @returns Promise resolving to the response data
 */
export async function executeGraphQLWithAuth<T = unknown>(
  query: string,
  variables?: Record<string, unknown>,
  getAccessToken?: () => Promise<string | null>,
  _requireAuth = false, // eslint-disable-line @typescript-eslint/no-unused-vars
): Promise<T> {
  return executeGraphQL<T>(query, variables, 'auto', getAccessToken);
}

/**
 * Helper function to check if an error is a GraphQL error
 */
export function isGraphQLError(error: unknown): error is GraphQLRequestError {
  return error instanceof GraphQLRequestError;
}

/**
 * Extract error message from a GraphQL error
 */
export function getGraphQLErrorMessage(error: unknown): string {
  if (isGraphQLError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

/**
 * Check if an error is an authentication error
 */
export function isAuthError(error: unknown): boolean {
  if (isGraphQLError(error)) {
    return (
      error.statusCode === 401 ||
      error.errors?.some(
        (err) =>
          err.extensions?.errorType === 'UnauthorizedException' ||
          err.extensions?.code === 'UNAUTHENTICATED' ||
          err.message.includes('Unauthorized'),
      ) ||
      false
    );
  }
  return false;
}
